/**
 * One registry for every keyboard binding the agent playground ships.
 *
 * The bindings themselves are declared in six different files (`useSessionShortcuts`,
 * `AgentConversation`, `SubmitPlugin`, `useRovingList`, `ApprovalCard`, `ConnectionDock`,
 * `ElicitationDock`, `usePushToTalk`). Nothing named them on screen, so nothing could drift.
 * Now that tooltips, menus and a shortcuts sheet all print the keys, they read them from here.
 */
import {localize, type TranslationKeys} from "@agenta/i18n"

import {isMacPlatform} from "./platform"

/** A modifier, named by role rather than by glyph — the glyph differs per platform. */
export type ShortcutModifier = "mod" | "alt" | "ctrl" | "shift"

/** Apple hardware prints glyphs; everything else prints words. */
const MODIFIER_FACE: Record<ShortcutModifier, {mac: string; other: string}> = {
    mod: {mac: "⌘", other: "Ctrl"},
    alt: {mac: "⌥", other: "Alt"},
    ctrl: {mac: "⌃", other: "Ctrl"},
    shift: {mac: "⇧", other: "Shift"},
}

export interface Shortcut {
    /** Stable id, used by a hint to look up the keys it must print. */
    id: string
    group: ShortcutGroupId
    /** What the shortcut does, in the words a user would use. */
    label: string
    modifiers?: ShortcutModifier[]
    /** The key face, already display-ready: `Z`, `1…9`, `↵`, `Esc`. */
    key: string
    /** A second chord that does the mirror action, e.g. next against previous. */
    alt?: {modifiers?: ShortcutModifier[]; key: string}
    /** When the binding only answers under a condition, say so in one clause. */
    when?: string
}

/**
 * A registry entry as declared: the words are catalog keys, so the table can stay a module
 * constant. {@link getShortcut} and {@link shortcutGroups} hand out a {@link Shortcut} with the
 * text resolved in the current language.
 */
export interface ShortcutDefinition extends Omit<Shortcut, "label" | "when"> {
    labelKey: TranslationKeys
    whenKey?: TranslationKeys
}

const toShortcut = ({labelKey, whenKey, ...chord}: ShortcutDefinition): Shortcut => ({
    ...chord,
    label: localize(labelKey),
    ...(whenKey ? {when: localize(whenKey)} : {}),
})

export type ShortcutGroupId =
    | "sessions"
    | "panels"
    | "run"
    | "composer"
    | "commands"
    | "mentions"
    | "picker"
    | "approval"
    | "connection"
    | "elicitation"
    | "voice"
    | "quote"
    | "rename"
    | "files"
    | "help"

/** Catalog keys, resolved by {@link shortcutGroups} when the sheet renders. */
export const SHORTCUT_GROUP_TITLES: Record<ShortcutGroupId, TranslationKeys> = {
    sessions: "com_nav_sessions",
    panels: "com_nav_shortcut_group_panels",
    run: "com_nav_shortcut_group_run",
    composer: "com_nav_shortcut_group_composer",
    commands: "com_nav_shortcut_group_commands",
    mentions: "com_nav_shortcut_group_mentions",
    picker: "com_nav_shortcut_group_picker",
    approval: "com_nav_shortcut_group_approval",
    connection: "com_nav_shortcut_group_connection",
    elicitation: "com_nav_shortcut_group_elicitation",
    voice: "com_nav_shortcut_group_voice",
    quote: "com_nav_shortcut_group_quote",
    rename: "com_nav_shortcut_group_rename",
    files: "com_nav_shortcut_group_files",
    help: "com_ui_help",
}

export const PLAYGROUND_SHORTCUTS: readonly ShortcutDefinition[] = [
    // Sessions — useSessionShortcuts.ts
    {
        id: "session.jump",
        group: "sessions",
        labelKey: "com_nav_shortcut_session_jump",
        modifiers: ["alt"],
        key: "1…9",
    },
    {
        id: "session.step",
        group: "sessions",
        labelKey: "com_nav_shortcut_session_step",
        modifiers: ["alt"],
        key: "Z",
        alt: {modifiers: ["alt"], key: "X"},
    },
    {
        id: "session.new",
        group: "sessions",
        labelKey: "com_nav_shortcut_session_new",
        modifiers: ["alt"],
        key: "+",
        whenKey: "com_nav_shortcut_session_new_when",
    },
    {
        id: "session.close",
        group: "sessions",
        labelKey: "com_nav_shortcut_session_close",
        modifiers: ["alt"],
        key: "W",
        whenKey: "com_nav_shortcut_session_close_when",
    },
    {
        id: "session.rename",
        group: "sessions",
        labelKey: "com_nav_shortcut_session_rename",
        modifiers: ["alt"],
        key: "R",
    },
    {
        id: "session.archive",
        group: "sessions",
        labelKey: "com_nav_shortcut_session_archive",
        modifiers: ["alt"],
        key: "A",
    },
    {
        id: "session.search",
        group: "sessions",
        labelKey: "com_nav_search_sessions",
        modifiers: ["alt"],
        key: "K",
    },
    // The mobile app's search-everything palette — MobileCommandPalette.tsx
    {
        id: "palette.open",
        group: "sessions",
        labelKey: "com_nav_shortcut_palette_open",
        modifiers: ["mod"],
        key: "K",
        whenKey: "com_nav_shortcut_palette_open_when",
    },

    // Side panels — ShowConfigPanelButton.tsx, OpenFilesPaneButton.tsx
    {
        id: "panel.config",
        group: "panels",
        labelKey: "com_nav_shortcut_panel_config",
        modifiers: ["alt"],
        key: "C",
    },
    {
        id: "panel.files",
        group: "panels",
        labelKey: "com_nav_shortcut_panel_files",
        modifiers: ["alt"],
        key: "O",
    },

    // Files pane — DriveExplorer.tsx, VirtualTileGrid.tsx, FolderList.tsx
    {
        id: "drive.move",
        group: "files",
        labelKey: "com_nav_shortcut_drive_move",
        key: "↑",
        alt: {key: "↓"},
    },
    {id: "drive.open", group: "files", labelKey: "com_nav_shortcut_drive_open", key: "↵"},
    {
        id: "drive.up",
        group: "files",
        labelKey: "com_nav_shortcut_drive_up",
        key: "⌫",
        alt: {modifiers: ["mod"], key: "↑"},
    },
    {
        id: "drive.close",
        group: "files",
        labelKey: "com_nav_shortcut_drive_close",
        key: "Esc",
        whenKey: "com_nav_shortcut_drive_close_when",
    },
    {
        id: "drive.back",
        group: "files",
        labelKey: "com_nav_shortcut_drive_back",
        modifiers: ["alt"],
        key: "←",
        alt: {modifiers: ["mod"], key: "["},
    },
    {
        id: "drive.forward",
        group: "files",
        labelKey: "com_nav_shortcut_drive_forward",
        modifiers: ["alt"],
        key: "→",
        alt: {modifiers: ["mod"], key: "]"},
    },
    {
        id: "drive.save",
        group: "files",
        labelKey: "com_nav_shortcut_drive_save",
        modifiers: ["mod"],
        key: "S",
        whenKey: "com_nav_shortcut_drive_save_when",
    },
    // Quote-to-reply — QuoteSelection/useQuoteSelection.ts
    {
        id: "quote.reply",
        group: "quote",
        labelKey: "com_nav_shortcut_quote_reply",
        modifiers: ["alt"],
        key: "Q",
        whenKey: "com_nav_shortcut_quote_reply_when",
    },
    {
        id: "quote.stage",
        group: "quote",
        labelKey: "com_nav_shortcut_quote_stage",
        key: "↵",
        whenKey: "com_nav_shortcut_quote_stage_when",
    },
    {
        id: "quote.send",
        group: "quote",
        labelKey: "com_nav_shortcut_quote_send",
        modifiers: ["mod"],
        key: "↵",
        whenKey: "com_nav_shortcut_quote_send_when",
    },
    {
        id: "quote.dismiss",
        group: "quote",
        labelKey: "com_nav_shortcut_quote_dismiss",
        key: "Esc",
    },
    // The running turn — AgentConversation.tsx
    {
        id: "run.stop",
        group: "run",
        labelKey: "com_nav_shortcut_run_stop",
        key: "Esc",
        whenKey: "com_nav_shortcut_run_stop_when",
    },
    {
        id: "run.approve",
        group: "run",
        labelKey: "com_nav_shortcut_run_approve",
        modifiers: ["alt"],
        key: "G",
        whenKey: "com_nav_shortcut_run_approve_when",
    },

    // Composer — RichChatInput / SubmitPlugin
    {id: "composer.send", group: "composer", labelKey: "com_ui_send", key: "↵"},
    {
        id: "composer.newline",
        group: "composer",
        labelKey: "com_nav_shortcut_composer_newline",
        modifiers: ["shift"],
        key: "↵",
    },
    {
        id: "composer.newlineMod",
        group: "composer",
        labelKey: "com_nav_shortcut_composer_newline_mod",
        modifiers: ["mod"],
        key: "↵",
    },
    {
        id: "composer.bold",
        group: "composer",
        labelKey: "com_nav_shortcut_composer_bold",
        modifiers: ["mod"],
        key: "B",
    },
    {
        id: "composer.italic",
        group: "composer",
        labelKey: "com_nav_shortcut_composer_italic",
        modifiers: ["mod"],
        key: "I",
    },
    {
        id: "composer.commands",
        group: "composer",
        labelKey: "com_nav_shortcut_composer_commands",
        key: "/",
    },
    {
        id: "composer.mentions",
        group: "composer",
        labelKey: "com_nav_shortcut_composer_mentions",
        key: "@",
    },

    // The / menu — CommandPalettePlugin.tsx. It binds no Home, End or ArrowLeft; those belong to
    // the permission picker below, which is a different surface with its own key handler.
    {
        id: "commands.move",
        group: "commands",
        labelKey: "com_nav_shortcut_commands_move",
        key: "↑",
        alt: {key: "↓"},
    },
    {
        id: "commands.pick",
        group: "commands",
        labelKey: "com_nav_shortcut_commands_pick",
        key: "↵",
        alt: {key: "⇥"},
    },
    {
        id: "commands.dismiss",
        group: "commands",
        labelKey: "com_nav_shortcut_commands_dismiss",
        key: "Esc",
    },

    // The @ file menu — the same plugin, with a folder level Tab enters and Esc steps back out of.
    {
        id: "mentions.move",
        group: "mentions",
        labelKey: "com_nav_shortcut_mentions_move",
        key: "↑",
        alt: {key: "↓"},
    },
    {id: "mentions.pick", group: "mentions", labelKey: "com_nav_shortcut_mentions_pick", key: "↵"},
    {id: "mentions.open", group: "mentions", labelKey: "com_nav_shortcut_mentions_open", key: "⇥"},
    {
        id: "mentions.back",
        group: "mentions",
        labelKey: "com_nav_shortcut_mentions_back",
        key: "Esc",
    },

    // The permission picker the / menu opens — useRovingList.ts
    {
        id: "picker.move",
        group: "picker",
        labelKey: "com_nav_shortcut_picker_move",
        key: "↑",
        alt: {key: "↓"},
    },
    {
        id: "picker.ends",
        group: "picker",
        labelKey: "com_nav_shortcut_picker_ends",
        key: "Home",
        alt: {key: "End"},
    },
    {id: "picker.pick", group: "picker", labelKey: "com_nav_shortcut_picker_pick", key: "↵"},
    {id: "picker.back", group: "picker", labelKey: "com_nav_shortcut_picker_back", key: "←"},
    {
        id: "picker.dismiss",
        group: "picker",
        labelKey: "com_nav_shortcut_picker_dismiss",
        key: "Esc",
    },

    // Approval card — ApprovalCard.tsx
    {
        id: "approval.approve",
        group: "approval",
        labelKey: "com_nav_shortcut_approval_approve",
        modifiers: ["mod"],
        key: "↵",
    },
    {
        id: "approval.deny",
        group: "approval",
        labelKey: "com_nav_shortcut_approval_deny",
        key: "Esc",
    },

    // Connection dock — ConnectionDock.tsx
    {
        id: "connection.connect",
        group: "connection",
        labelKey: "com_ui_connect",
        modifiers: ["mod"],
        key: "↵",
    },
    {
        id: "connection.decline",
        group: "connection",
        labelKey: "com_nav_shortcut_connection_decline",
        key: "Esc",
    },

    // Elicitation dock — ElicitationDock.tsx and its controls
    {
        id: "form.next",
        group: "elicitation",
        labelKey: "com_nav_shortcut_form_next",
        modifiers: ["mod"],
        key: "↵",
    },
    {
        id: "form.step",
        group: "elicitation",
        labelKey: "com_nav_shortcut_form_step",
        modifiers: ["mod"],
        key: "←",
        alt: {modifiers: ["mod"], key: "→"},
    },
    {
        id: "form.skip",
        group: "elicitation",
        labelKey: "com_nav_shortcut_form_skip",
        modifiers: ["mod"],
        key: "⌫",
    },
    {id: "form.pick", group: "elicitation", labelKey: "com_nav_shortcut_form_pick", key: "1…9"},
    {
        id: "form.move",
        group: "elicitation",
        labelKey: "com_nav_shortcut_form_move",
        key: "↑",
        alt: {key: "↓"},
    },
    {
        id: "form.ends",
        group: "elicitation",
        labelKey: "com_nav_shortcut_form_ends",
        key: "Home",
        alt: {key: "End"},
    },
    {
        id: "form.toggle",
        group: "elicitation",
        labelKey: "com_nav_shortcut_form_toggle",
        key: "Space",
        whenKey: "com_nav_shortcut_form_toggle_when",
    },
    {id: "form.enter", group: "elicitation", labelKey: "com_nav_shortcut_form_enter", key: "↵"},
    {id: "form.escape", group: "elicitation", labelKey: "com_nav_shortcut_form_escape", key: "Esc"},
    {
        id: "form.chipAdd",
        group: "elicitation",
        labelKey: "com_nav_shortcut_form_chip_add",
        key: "↵",
        alt: {key: ","},
    },
    {
        id: "form.chipRemove",
        group: "elicitation",
        labelKey: "com_nav_shortcut_form_chip_remove",
        key: "⌫",
        whenKey: "com_nav_shortcut_form_chip_remove_when",
    },

    // Voice — usePushToTalk.ts, RecordingBar.tsx
    {
        id: "voice.hold",
        group: "voice",
        labelKey: "com_nav_shortcut_voice_hold",
        modifiers: ["ctrl", "alt"],
        key: "",
    },
    {id: "voice.cancel", group: "voice", labelKey: "com_nav_shortcut_voice_cancel", key: "Esc"},

    // Help — KeyboardShortcutsSheet.tsx
    {id: "help.sheet", group: "help", labelKey: "com_nav_keyboard_shortcuts", key: "?"},

    // Inline rename — SessionTabLabel.tsx
    {id: "rename.commit", group: "rename", labelKey: "com_nav_shortcut_rename_commit", key: "↵"},
    {id: "rename.cancel", group: "rename", labelKey: "com_nav_shortcut_rename_cancel", key: "Esc"},
]

const BY_ID = new Map(PLAYGROUND_SHORTCUTS.map((shortcut) => [shortcut.id, shortcut]))

/** Look one up by id. Returns undefined rather than throwing, so a hint degrades to no hint. */
export const getShortcut = (id: string): Shortcut | undefined => {
    const definition = BY_ID.get(id)
    return definition && toShortcut(definition)
}

/** The groups in display order, each with its shortcuts. */
export const shortcutGroups = (): {id: ShortcutGroupId; title: string; shortcuts: Shortcut[]}[] =>
    (Object.keys(SHORTCUT_GROUP_TITLES) as ShortcutGroupId[]).map((id) => ({
        id,
        title: localize(SHORTCUT_GROUP_TITLES[id]),
        shortcuts: PLAYGROUND_SHORTCUTS.filter((shortcut) => shortcut.group === id).map(toShortcut),
    }))

/**
 * The key faces to print, in order, for this platform. Pass `mac` explicitly from a mount effect;
 * during render on the server there is no platform to read and a guess mismatches on hydration.
 */
export const shortcutFaces = (
    chord: {modifiers?: ShortcutModifier[]; key: string},
    mac = isMacPlatform(),
): string[] => {
    const modifiers = (chord.modifiers ?? []).map((m) =>
        mac ? MODIFIER_FACE[m].mac : MODIFIER_FACE[m].other,
    )
    return chord.key ? [...modifiers, chord.key] : modifiers
}

/** Display glyph to the name `aria-keyshortcuts` expects. Anything absent passes through. */
const ARIA_KEY: Record<string, string> = {
    "↵": "Enter",
    Esc: "Escape",
    "⌫": "Backspace",
    "⇥": "Tab",
    "←": "ArrowLeft",
    "→": "ArrowRight",
    "↑": "ArrowUp",
    "↓": "ArrowDown",
}

/** Key faces that `aria-keyshortcuts` cannot express honestly.
 *
 * `+` is the attribute's own joiner, so "Alt++" is ambiguous. `?` is the character a layout
 * produces, not a key: it is Shift+/ on a US keyboard, Shift+ß on a German one. The attribute wants
 * physical keys and their modifiers, so for these we emit nothing and let the visible keycap and
 * the shortcuts sheet carry the hint instead. */
const ARIA_INEXPRESSIBLE = new Set(["+", "?"])

/** Modifier to its ARIA name. `mod` has no single name, so it expands to both alternatives. */
const ARIA_MODIFIER: Record<ShortcutModifier, string[]> = {
    mod: ["Meta", "Control"],
    alt: ["Alt"],
    ctrl: ["Control"],
    shift: ["Shift"],
}

/**
 * The value for `aria-keyshortcuts`, built from the same entry the keycaps are drawn from, so a
 * moved key updates the attribute too. Returns undefined, not "", for a range like `1…9` or a
 * modifier-only hold: React omits an undefined attribute, while "" would render an empty one that
 * names no key at all.
 */
export const shortcutAria = (id: string): string | undefined => {
    const shortcut = BY_ID.get(id)
    if (!shortcut) return undefined
    const chords = [shortcut, ...(shortcut.alt ? [shortcut.alt] : [])]
    const values: string[] = []
    for (const chord of chords) {
        if (!chord.key || chord.key.includes("…") || ARIA_INEXPRESSIBLE.has(chord.key)) continue
        const key = ARIA_KEY[chord.key] ?? chord.key
        // Every combination of the alternatives: `mod` alone already yields Meta and Control.
        let combos: string[][] = [[]]
        for (const modifier of chord.modifiers ?? []) {
            combos = combos.flatMap((combo) =>
                ARIA_MODIFIER[modifier].map((name) => [...combo, name]),
            )
        }
        for (const combo of combos) values.push([...combo, key].join("+"))
    }
    return values.length ? values.join(" ") : undefined
}

/** A flat text label, for a tooltip title. */
export const shortcutText = (
    chord: {modifiers?: ShortcutModifier[]; key: string},
    mac = isMacPlatform(),
): string => shortcutFaces(chord, mac).join(mac ? "" : "+")
